import React, { useState, useEffect, useCallback } from "react";
import {
  getDocs,
  orderBy,
  query,
  limit,
  doc,
  getDoc,
  where,
  startAfter,
  collectionGroup,
  limitToLast,
  endBefore,
} from "firebase/firestore";
import { firestoreProd } from "../../firebase";
import {
  FaSearch,
  FaChevronDown,
  FaChevronRight,
  FaTasks,
  FaClock,
  FaExclamationTriangle,
  FaCheckCircle,
  FaSpinner,
  FaFilter,
  FaChevronLeft,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ITEMS_PER_PAGE = 25;

const ProcessingMonitoringPage = () => {
  const [loans, setLoans] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [expandedLoans, setExpandedLoans] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all, processing, completed, error
  const [dateRange, setDateRange] = useState("week"); // today, week, month, all
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [firstVisible, setFirstVisible] = useState(null);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);
  const [pageCache, setPageCache] = useState({}); // Cache for visited pages
  
  const [statistics, setStatistics] = useState({
    total: 0,
    processing: 0,
    completed: 0,
    error: 0,
  });

  // Compute filtered loans based on search input and filters
  const filteredLoans = loans.filter((loan) => {
    if (!searchTerm.trim() && statusFilter === "all") return true;

    const searchLower = searchTerm.toLowerCase();
    const orgIdMatch = loan.organizationId?.toLowerCase().includes(searchLower);
    const orgNameMatch = loan.organizationName
      ?.toLowerCase()
      .includes(searchLower);
    const loanIdMatch = loan.id?.toLowerCase().includes(searchLower);
    const loanNameMatch = loan.name?.toLowerCase().includes(searchLower);

    const statusMatch =
      statusFilter === "all" ||
      loan.neededDocumentsProcessingStatus === statusFilter;

    const searchMatch =
      !searchTerm.trim() ||
      orgIdMatch ||
      orgNameMatch ||
      loanIdMatch ||
      loanNameMatch;

    return searchMatch && statusMatch;
  });

  const fetchOrganizations = useCallback(async (organizationIds) => {
    const organizationsMap = {};

    if (!organizationIds || organizationIds.length === 0) {
      return organizationsMap;
    }

    try {
      const fetchPromises = organizationIds.map(async (orgId) => {
        try {
          const orgRef = doc(firestoreProd, "organizations", orgId);
          const orgSnapshot = await getDoc(orgRef);

          if (orgSnapshot.exists()) {
            const orgData = orgSnapshot.data();
            return { id: orgId, name: orgData.name || orgId };
          }
          return { id: orgId, name: null };
        } catch (error) {
          console.warn(`Error fetching organization ${orgId}:`, error);
          return { id: orgId, name: null };
        }
      });

      const orgResults = await Promise.all(fetchPromises);
      orgResults.forEach(({ id, name }) => {
        organizationsMap[id] = name;
      });
    } catch (error) {
      console.error("Error fetching organizations:", error);
    }

    return organizationsMap;
  }, []);

  const fetchLoansWithProcessing = useCallback(
    async (pageNumber = 1, direction = "first") => {
      if (!firestoreProd) {
        toast.error("Firebase is not initialized");
        return;
      }

      // Check cache first
      const cacheKey = `${pageNumber}-${statusFilter}-${dateRange}`;
      if (pageCache[cacheKey]) {
        const cached = pageCache[cacheKey];
        setLoans(cached.loans);
        setFirstVisible(cached.firstVisible);
        setLastVisible(cached.lastVisible);
        setHasNextPage(cached.hasNextPage);
        setHasPrevPage(pageNumber > 1);
        setCurrentPage(pageNumber);
        return;
      }

      setIsLoading(true);

      try {
        // Build base query using collectionGroup to query across all organizations
        // Apply status filter at query level
        let statusesToQuery = ["processing", "completed", "error"];
        if (statusFilter !== "all") {
          statusesToQuery = [statusFilter];
        }

        let q = query(
          collectionGroup(firestoreProd, "loans"),
          where("neededDocumentsProcessingStatus", "in", statusesToQuery),
          orderBy("neededDocumentsProcessingStartedAt", "desc")
        );

        // Apply date range filter if needed
        if (dateRange !== "all") {
          let dateThreshold = null;
          const now = Date.now();
          if (dateRange === "today") {
            const todayStart = new Date();
            todayStart.setHours(0, 0, 0, 0);
            dateThreshold = todayStart.getTime();
          } else if (dateRange === "week") {
            dateThreshold = now - 7 * 24 * 60 * 60 * 1000;
          } else if (dateRange === "month") {
            dateThreshold = now - 30 * 24 * 60 * 60 * 1000;
          }
          
          if (dateThreshold) {
            q = query(q, where("neededDocumentsProcessingStartedAt", ">=", dateThreshold));
          }
        }

        // Apply pagination cursors
        if (direction === "next" && lastVisible) {
          q = query(q, startAfter(lastVisible), limit(ITEMS_PER_PAGE + 1));
        } else if (direction === "prev" && firstVisible) {
          q = query(q, endBefore(firstVisible), limitToLast(ITEMS_PER_PAGE + 1));
        } else {
          // First page or no cursor
          q = query(q, limit(ITEMS_PER_PAGE + 1));
        }

        const loansSnapshot = await getDocs(q);
        const docs = loansSnapshot.docs;

        // Check if there are more pages
        const hasMore = docs.length > ITEMS_PER_PAGE;
        const loansToShow = hasMore ? docs.slice(0, ITEMS_PER_PAGE) : docs;

        // Extract loan data with organization ID from document path
        const loansData = loansToShow.map((doc) => {
          // Extract organizationId from the document reference path
          // Path format: organizations/{orgId}/loans/{loanId}
          const pathParts = doc.ref.path.split("/");
          const orgId = pathParts[1];
          
          return {
            id: doc.id,
            organizationId: orgId,
            ...doc.data(),
          };
        });

        // Get unique organization IDs
        const organizationIds = [...new Set(loansData.map(loan => loan.organizationId))];

        // Fetch organization names
        const organizationsMap = await fetchOrganizations(organizationIds);

        // Add organization names to loans
        const loansWithOrgNames = loansData.map((loan) => ({
          ...loan,
          organizationName: organizationsMap[loan.organizationId] || null,
        }));

        setLoans(loansWithOrgNames);
        
        // Set pagination cursors
        if (loansToShow.length > 0) {
          setFirstVisible(loansToShow[0]);
          setLastVisible(loansToShow[loansToShow.length - 1]);
        }
        
        setHasNextPage(hasMore);
        setHasPrevPage(pageNumber > 1);
        setCurrentPage(pageNumber);

        // Cache this page
        setPageCache((prev) => ({
          ...prev,
          [cacheKey]: {
            loans: loansWithOrgNames,
            firstVisible: loansToShow[0],
            lastVisible: loansToShow[loansToShow.length - 1],
            hasNextPage: hasMore,
          },
        }));

        setHasLoaded(true);
        
        if (loansWithOrgNames.length > 0) {
          toast.success(
            `Loaded ${loansWithOrgNames.length} loans (Page ${pageNumber})`
          );
        }
      } catch (error) {
        console.error("Error fetching loans:", error);
        toast.error("Failed to fetch loan processing data");
      } finally {
        setIsLoading(false);
      }
    },
    [fetchOrganizations, dateRange, statusFilter, lastVisible, firstVisible, pageCache]
  );

  // Fetch statistics separately (just counts, not all data)
  const fetchStatistics = useCallback(async () => {
    if (!firestoreProd) return;

    try {
      // Query for counts - we'll fetch a reasonable sample to estimate
      const q = query(
        collectionGroup(firestoreProd, "loans"),
        where("neededDocumentsProcessingStatus", "in", [
          "processing",
          "completed",
          "error",
        ]),
        limit(500) // Sample size for statistics
      );

      const snapshot = await getDocs(q);
      const docs = snapshot.docs;

      const stats = {
        total: docs.length,
        processing: docs.filter(
          (doc) => doc.data().neededDocumentsProcessingStatus === "processing"
        ).length,
        completed: docs.filter(
          (doc) => doc.data().neededDocumentsProcessingStatus === "completed"
        ).length,
        error: docs.filter(
          (doc) => doc.data().neededDocumentsProcessingStatus === "error"
        ).length,
      };

      // Add a note if we hit the limit
      if (docs.length === 500) {
        stats.total = "500+";
      }

      setStatistics(stats);
    } catch (error) {
      console.error("Error fetching statistics:", error);
    }
  }, []);

  // Load loans and statistics on component mount and when filters change
  useEffect(() => {
    // Reset to page 1 when filters change
    setCurrentPage(1);
    setPageCache({});
    setFirstVisible(null);
    setLastVisible(null);
    fetchLoansWithProcessing(1, "first");
    fetchStatistics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, statusFilter]);

  // Fetch statistics on mount
  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  // Pagination handlers
  const handleNextPage = () => {
    if (hasNextPage && !isLoading) {
      fetchLoansWithProcessing(currentPage + 1, "next");
    }
  };

  const handlePrevPage = () => {
    if (hasPrevPage && !isLoading) {
      fetchLoansWithProcessing(currentPage - 1, "prev");
    }
  };

  const toggleLoanExpansion = (loanId) => {
    setExpandedLoans((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(loanId)) {
        newSet.delete(loanId);
      } else {
        newSet.add(loanId);
      }
      return newSet;
    });
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const formatDuration = (startTimestamp, endTimestamp) => {
    if (!startTimestamp || !endTimestamp) return "N/A";
    const durationMs = endTimestamp - startTimestamp;
    const seconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "processing":
        return <span className="badge bg-primary">Processing</span>;
      case "completed":
        return <span className="badge bg-success">Completed</span>;
      case "error":
        return <span className="badge bg-danger">Error</span>;
      default:
        return <span className="badge bg-secondary">Unknown</span>;
    }
  };

  return (
    <div className="container-fluid p-4 ProcessingMonitoringPageContainer">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <h1>
        <FaTasks className="me-2" />
        Processing Checklist & Needed Documents Monitoring
      </h1>

      {/* Statistics Cards */}
      <div className="row mb-4">
        <div className="col-md-3">
          <div className="card text-center">
            <div className="card-body">
              <h5 className="card-title">Total Processed</h5>
              <h2 className="mb-0">{statistics.total}</h2>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card text-center border-primary">
            <div className="card-body">
              <FaSpinner className="text-primary me-2" />
              <h5 className="card-title">Processing</h5>
              <h2 className="mb-0 text-primary">{statistics.processing}</h2>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card text-center border-success">
            <div className="card-body">
              <FaCheckCircle className="text-success me-2" />
              <h5 className="card-title">Completed</h5>
              <h2 className="mb-0 text-success">{statistics.completed}</h2>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card text-center border-danger">
            <div className="card-body">
              <FaExclamationTriangle className="text-danger me-2" />
              <h5 className="card-title">Errors</h5>
              <h2 className="mb-0 text-danger">{statistics.error}</h2>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">
            <FaFilter className="me-2" />
            Filters
          </h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-4">
              <label htmlFor="statusFilter" className="form-label">
                Status
              </label>
              <select
                id="statusFilter"
                className="form-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="processing">Processing</option>
                <option value="completed">Completed</option>
                <option value="error">Error</option>
              </select>
            </div>
            <div className="col-md-4">
              <label htmlFor="dateRange" className="form-label">
                Date Range
              </label>
              <select
                id="dateRange"
                className="form-select"
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
              >
                <option value="today">Today</option>
                <option value="week">Last 7 Days</option>
                <option value="month">Last 30 Days</option>
                <option value="all">All Time</option>
              </select>
            </div>
            <div className="col-md-4">
              <label htmlFor="searchInput" className="form-label">
                Search
              </label>
              <div className="input-group">
                <span className="input-group-text">
                  <FaSearch />
                </span>
                <input
                  type="text"
                  className="form-control"
                  id="searchInput"
                  placeholder="Search by org, loan ID, or name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    onClick={() => setSearchTerm("")}
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Loans Table */}
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">Processing History</h5>
          {isLoading && (
            <div className="mt-2">
              <div
                className="spinner-border spinner-border-sm me-2"
                role="status"
              ></div>
              <span className="text-muted">Loading loans...</span>
            </div>
          )}
          {loans.length > 0 && (
            <div className="mt-2">
              <span className="text-muted">
                Showing {filteredLoans.length} loans on page {currentPage}
              </span>
            </div>
          )}
        </div>
        <div className="card-body">
          {filteredLoans.length > 0 && (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead className="table-light">
                  <tr>
                    <th width="50"></th>
                    <th>Organization</th>
                    <th>Loan</th>
                    <th>Status</th>
                    <th>Started</th>
                    <th>Duration</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLoans.map((loan) => {
                    const isExpanded = expandedLoans.has(loan.id);
                    const duration = formatDuration(
                      loan.neededDocumentsProcessingStartedAt,
                      loan.neededDocumentsProcessingCompletedAt
                    );

                    return (
                      <React.Fragment key={`${loan.organizationId}-${loan.id}`}>
                        {/* Main Row */}
                        <tr
                          className="cursor-pointer"
                          onClick={() =>
                            toggleLoanExpansion(
                              `${loan.organizationId}-${loan.id}`
                            )
                          }
                          style={{ cursor: "pointer" }}
                        >
                          <td>
                            {isExpanded ? (
                              <FaChevronDown className="text-muted" />
                            ) : (
                              <FaChevronRight className="text-muted" />
                            )}
                          </td>
                          <td>
                            {loan.organizationName ? (
                              <div>
                                <div>{loan.organizationName}</div>
                                <small className="text-muted font-monospace">
                                  {loan.organizationId}
                                </small>
                              </div>
                            ) : (
                              <small className="font-monospace">
                                {loan.organizationId}
                              </small>
                            )}
                          </td>
                          <td>
                            <div>
                              {loan.name && <div>{loan.name}</div>}
                              <small className="text-muted font-monospace">
                                {loan.id}
                              </small>
                            </div>
                          </td>
                          <td>
                            {getStatusBadge(
                              loan.neededDocumentsProcessingStatus
                            )}
                          </td>
                          <td>
                            <FaClock className="me-1 text-muted" />
                            {formatTimestamp(
                              loan.neededDocumentsProcessingStartedAt
                            )}
                          </td>
                          <td>{duration}</td>
                          <td>
                            <a
                              href={`https://app.addy.so/loans?tab=board&loan=${loan.id}&person=example%40example.com&tabValue=processingChecklist`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-outline-primary"
                              onClick={(e) => e.stopPropagation()}
                            >
                              View Loan
                            </a>
                          </td>
                        </tr>

                        {/* Expanded Row - Details */}
                        {isExpanded && (
                          <tr>
                            <td colSpan="7" className="bg-light">
                              <div className="p-3">
                                <h6 className="mb-3">Processing Details</h6>

                                <div className="row">
                                  <div className="col-md-6">
                                    <h6>Timeline</h6>
                                    <ul className="list-unstyled">
                                      <li>
                                        <strong>Started:</strong>{" "}
                                        {formatTimestamp(
                                          loan.neededDocumentsProcessingStartedAt
                                        )}
                                      </li>
                                      {loan.neededDocumentsProcessingCompletedAt && (
                                        <li>
                                          <strong>Completed:</strong>{" "}
                                          {formatTimestamp(
                                            loan.neededDocumentsProcessingCompletedAt
                                          )}
                                        </li>
                                      )}
                                      <li>
                                        <strong>Duration:</strong> {duration}
                                      </li>
                                    </ul>
                                  </div>

                                  <div className="col-md-6">
                                    <h6>Status Information</h6>
                                    <ul className="list-unstyled">
                                      <li>
                                        <strong>Status:</strong>{" "}
                                        {getStatusBadge(
                                          loan.neededDocumentsProcessingStatus
                                        )}
                                      </li>
                                      {loan.neededDocumentsProcessingError && (
                                        <li className="mt-2">
                                          <div className="alert alert-danger mb-0">
                                            <strong>
                                              <FaExclamationTriangle className="me-2" />
                                              Error:
                                            </strong>
                                            <p className="mb-0 mt-1">
                                              {loan.neededDocumentsProcessingError}
                                            </p>
                                          </div>
                                        </li>
                                      )}
                                    </ul>
                                  </div>
                                </div>

                                <div className="mt-3">
                                  <h6>Loan Information</h6>
                                  <ul className="list-unstyled">
                                    {loan.authorName && (
                                      <li>
                                        <strong>Author:</strong>{" "}
                                        {loan.authorName}
                                      </li>
                                    )}
                                    {loan.authorEmail && (
                                      <li>
                                        <strong>Email:</strong>{" "}
                                        {loan.authorEmail}
                                      </li>
                                    )}
                                    {loan.borrowers &&
                                      loan.borrowers.length > 0 && (
                                        <li>
                                          <strong>Borrowers:</strong>{" "}
                                          {loan.borrowers
                                            .map(
                                              (b) =>
                                                `${b.firstName || ""} ${
                                                  b.lastName || ""
                                                }`.trim() || b.email
                                            )
                                            .join(", ")}
                                        </li>
                                      )}
                                  </ul>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {filteredLoans.length === 0 && loans.length > 0 && (
            <div className="alert alert-info" role="alert">
              <FaSearch className="me-2" />
              No loans found matching your filters. Try adjusting your search or
              filters.
            </div>
          )}

          {loans.length === 0 && hasLoaded && !isLoading && (
            <div className="alert alert-info" role="alert">
              No loans with processing data found.
            </div>
          )}

          {!hasLoaded && !isLoading && (
            <div className="alert alert-info" role="alert">
              Loading processing data...
            </div>
          )}

          {/* Pagination Controls */}
          {loans.length > 0 && (hasPrevPage || hasNextPage) && (
            <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
              <button
                className="btn btn-outline-primary"
                onClick={handlePrevPage}
                disabled={!hasPrevPage || isLoading}
              >
                <FaChevronLeft className="me-2" />
                Previous
              </button>
              
              <div className="text-muted">
                Page {currentPage}
                {hasNextPage && " of many"}
              </div>
              
              <button
                className="btn btn-outline-primary"
                onClick={handleNextPage}
                disabled={!hasNextPage || isLoading}
              >
                Next
                <FaChevronRight className="ms-2" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProcessingMonitoringPage;

